import type { QuerySpec } from '@helios/powersync-client-core';

/* ------------------------------------------------------------------ */
/*  Notification client queries (read-only via PowerSync)            */
/*                                                                    */
/*  Server-side helpers (createNotificationJob, claimDueWhatsAppJobs, */
/*  insertNotificationDelivery, …) live in @helios/whatsapp-core/db. */
/*  This file only exposes client-side QuerySpecs over the synced     */
/*  notification_jobs / notification_deliveries shadow tables.        */
/* ------------------------------------------------------------------ */

export interface NotificationJobClientRow {
  id: string;
  org_id: string | null;
  channel: string | null;
  template_id: string | null;
  patient_id: string | null;
  to_phone_e164: string | null;
  to_market: string | null;
  scheduled_at: string | null;
  next_attempt_at: string | null;
  status: string | null;
  attempts: number | null;
  max_attempts: number | null;
  last_error: string | null;
  client_reference: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface NotificationDeliveryClientRow {
  id: string;
  notification_job_id: string | null;
  org_id: string | null;
  channel: string | null;
  provider_name: string | null;
  attempt: number | null;
  status: string | null;
  provider_message_id: string | null;
  provider_status: string | null;
  error_code: string | null;
  error_message: string | null;
  estimated_billable: number | null;
  estimated_price_amount: number | null;
  estimated_price_currency: string | null;
  pricing_category: string | null;
  pricing_market: string | null;
  pricing_basis: string | null;
  template_category: string | null;
  created_at: string | null;
}

export interface WhatsappUsageSummaryFilters {
  /** Inclusive ISO start date (e.g. "2026-05-01"). */
  fromDate?: string;
  /** Inclusive ISO end date (e.g. "2026-05-31"). */
  toDate?: string;
  /** Optional pricing category filter. */
  category?: string;
  /** Optional pricing market filter. */
  market?: string;
}

export interface WhatsappUsageSummaryRow {
  pricing_category: string | null;
  pricing_market: string | null;
  attempts: number | null;
  accepted: number | null;
  estimated_total_amount: number | null;
  estimated_currency: string | null;
}

export function qNotificationJobsByPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['notification-jobs', 'by-patient', patientId] as const,
    sql: `SELECT * FROM notification_jobs WHERE patient_id = ? ORDER BY scheduled_at DESC`,
    params: [patientId],
    watch: ['notification_jobs'],
  };
}

export function qNotificationDeliveriesByJob(jobId: string): QuerySpec<[string]> {
  return {
    key: ['notification-deliveries', 'by-job', jobId] as const,
    sql: `SELECT * FROM notification_deliveries WHERE notification_job_id = ? ORDER BY attempt ASC`,
    params: [jobId],
    watch: ['notification_deliveries'],
  };
}

/**
 * Aggregate WhatsApp usage by pricing category + market over a date window.
 *
 * V1 totals are estimates only — see PRD.211 §"Cost metadata".
 */
export function qWhatsappUsageSummary(
  filters: WhatsappUsageSummaryFilters = {},
): QuerySpec<string[]> {
  const conditions: string[] = [`channel = 'whatsapp'`];
  const params: string[] = [];

  if (filters.fromDate) {
    conditions.push(`created_at >= ?`);
    params.push(filters.fromDate);
  }
  if (filters.toDate) {
    conditions.push(`created_at <= ?`);
    params.push(filters.toDate);
  }
  if (filters.category) {
    conditions.push(`pricing_category = ?`);
    params.push(filters.category);
  }
  if (filters.market) {
    conditions.push(`pricing_market = ?`);
    params.push(filters.market);
  }

  const where = conditions.join(' AND ');

  return {
    key: [
      'notification-deliveries',
      'whatsapp-usage-summary',
      filters.fromDate ?? '',
      filters.toDate ?? '',
      filters.category ?? '',
      filters.market ?? '',
    ] as const,
    sql: `
      SELECT
        pricing_category,
        pricing_market,
        COUNT(*) AS attempts,
        SUM(CASE WHEN status = 'accepted' THEN 1 ELSE 0 END) AS accepted,
        SUM(COALESCE(estimated_price_amount, 0)) AS estimated_total_amount,
        MAX(estimated_price_currency) AS estimated_currency
      FROM notification_deliveries
      WHERE ${where}
      GROUP BY pricing_category, pricing_market
      ORDER BY pricing_category ASC, pricing_market ASC
    `,
    params,
    watch: ['notification_deliveries'],
  };
}

/* ------------------------------------------------------------------ */
/*  Patient communication preferences                                 */
/* ------------------------------------------------------------------ */

export interface PatientCommunicationPreferencesClientRow {
  id: string;
  org_id: string | null;
  patient_id: string | null;
  whatsapp_opt_in: number | null; // 0/1
  whatsapp_opt_out_at: string | null;
  whatsapp_opt_out_reason: string | null;
  sms_opt_in: number | null; // 0/1
  email_opt_in: number | null; // 0/1
  created_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

/** One row per (org, patient). Drives the WhatsApp opt-in gate in the UI. */
export function qPatientCommunicationPreferences(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient-communication-preferences', 'by-patient', patientId] as const,
    sql: `SELECT * FROM patient_communication_preferences WHERE patient_id = ? LIMIT 1`,
    params: [patientId],
    watch: ['patient_communication_preferences'],
  };
}
